import { BatchedMesh, Vector4, type BufferGeometry, type Material, type Matrix4 } from 'three';
import { UNPOSED } from './citizenPose';
import { createContactShadows } from '../../../render/contactShadows';

/**
 * Citizens drawn in one batch and one draw: any number of each figure, each where its matrix puts
 * it and in a pose of its own. three gives a batch one colour an instance; a batch of citizens
 * gives it the instance's pose instead, which is what makes the painted material pose its vertices
 * (render/shaders/painted.vert).
 */
export interface CitizenBatch {
  readonly mesh: BatchedMesh;
  readonly capacity: number;
  /** Starts a frame with nobody drawn. */
  begin(): void;
  /** Draws one figure `mix` of the way from the pose `from` to the pose `to` (citizenPose.ts);
   *  false when the batch is full. */
  draw(shape: number, matrix: Matrix4, from: number, to?: number, mix?: number): boolean;
  /** Ends the frame: hides whom the last frame drew and this one did not. */
  end(): number;
}

/** A batch of `shapes` (citizenShapes.ts), each drawn any number of times up to `capacity` in all. */
export function createCitizenBatch(
  material: Material,
  shapes: readonly BufferGeometry[],
  capacity: number,
  name: string
): CitizenBatch {
  const mesh = new BatchedMesh(
    capacity,
    shapes.reduce((n, shape) => n + shape.getAttribute('position').count, 0),
    0,
    material
  );
  mesh.name = name;
  const shadows = createContactShadows(capacity, material);
  mesh.add(shadows.root);
  mesh.addEventListener('dispose', () => {
    shadows.dispose();
  });
  mesh.frustumCulled = false;
  mesh.perObjectFrustumCulled = false;
  mesh.sortObjects = false;
  const ids = shapes.map((shape) => {
    const id = mesh.addGeometry(shape);
    // The batch keeps its own copy.
    shape.dispose();
    return id;
  });
  const pose = new Vector4(UNPOSED, UNPOSED, 0, 1);
  for (let i = 0; i < capacity; i++) {
    const instance = mesh.addInstance(ids[0]!);
    mesh.setVisibleAt(instance, false);
    // three types an instance's colour as a Color; it stores whatever four numbers it is given.
    mesh.setColorAt(instance, pose);
  }
  mesh.visible = false;
  let used = 0,
    before = 0;
  return {
    mesh,
    capacity,
    begin() {
      shadows.begin();
      before = used;
      used = 0;
    },
    draw(shape, matrix, from, to = from, mix = 0) {
      if (used >= capacity) return false;
      mesh.setGeometryIdAt(used, ids[shape]!);
      mesh.setMatrixAt(used, matrix);
      mesh.setColorAt(used, pose.set(from, to, mix, 1));
      mesh.setVisibleAt(used, true);
      shadows.add(matrix);
      used++;
      return true;
    },
    end() {
      shadows.end();
      for (let i = used; i < before; i++) mesh.setVisibleAt(i, false);
      mesh.visible = used > 0;
      return used;
    },
  };
}
